/* __next_internal_client_entry_do_not_use__ ScrollButton,Default,SHARED_PLACEHOLDER_VARIANTS auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
// Global
import { useState, useEffect } from 'react';
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { findComponent, getParentSectionFields } from 'lib/utils/object-utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { TabContextProvider, useTab } from 'helpers/Context/TabContext';
import { SvgIcon } from 'helpers/SvgIcon';
import { useScrollableTabs } from 'lib/hooks/useScrollableTabs';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useTextAlignment } from 'lib/context/TextAlignmentContext';
import { getTestProps } from 'lib/testing/utils';
import { createTabClickGTMEvent } from 'lib/utils/gtm-utils';
import { sendGTMEvent } from '@next/third-parties/google';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
export const ScrollButton = ({ direction, onClick, tabListId })=>{
    const { tabArrows } = TAILWIND_VARIANTS();
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };
    return /*#__PURE__*/ _jsx("button", {
        className: tabArrows({
            left: direction === 'left',
            right: direction === 'right'
        }),
        onClick: onClick,
        onKeyDown: handleKeyDown,
        "aria-label": `Scroll tabs ${direction}`,
        "aria-controls": tabListId,
        tabIndex: 0,
        children: /*#__PURE__*/ _jsx(SvgIcon, {
            icon: `arrow-dash-${direction}-thin`,
            size: "s",
            viewBox: '0 0 20 20',
            fill: "currentColor"
        })
    });
};
const TestimonialTabContent = (props)=>{
    const { DynamicPlaceholderId, RenderingIdentifier } = props?.params || {};
    const { activeTab, setActiveTab } = useTab();
    const { getDictionaryValue } = useDictionary();
    const isEditing = useIsEditing();
    const { containerRef, isScrollable, isLeftArrowVisible, isRightArrowVisible, handleScroll } = useScrollableTabs();
    const phKey = `testimonialtab-${DynamicPlaceholderId}`;
    const tabItems = findComponent(props.rendering, 'TestimonialTabItem') || [];
    const hasMultipleItems = tabItems.length > 1;
    const shouldShowTabs = hasMultipleItems || isEditing;
    // Check for section wrapper alignment via context
    const { textAlignment } = useTextAlignment();
    const isCentered = textAlignment === 'Center';
    // Create unique IDs for accessibility using component instance
    // Use useState with empty string to prevent hydration mismatch
    const baseId = RenderingIdentifier || props?.rendering?.uid || 'default';
    const [tabListId, setTabListId] = useState(`tab-list-${baseId}`);
    // Generate unique ID only on client side to prevent hydration mismatch
    useEffect(()=>{
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 5);
        setTabListId(`tab-list-${baseId}-${timestamp}-${random}`);
    }, [
        baseId
    ]);
    // Get Sitecore context for component searching
    const { sitecoreContext } = useSitecoreContext();
    const fields = getParentSectionFields(sitecoreContext ?? {}, props?.rendering?.uid || '');
    const sectionTitle = fields?.title?.value ?? '';
    const { base, tabsContainerWrapper, tabsWrapper, tabLinkWrapper, tabLink, tabHeader, tabImage, tabText } = TAILWIND_VARIANTS();
    const { imagePlaceholder } = SHARED_PLACEHOLDER_VARIANTS({
        size: 'full',
        isActive: false
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: base(),
        "data-component": "authorable/shared/lists/TestimonialTabs",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-tab-${props?.rendering?.uid}`),
        children: [
            shouldShowTabs && /*#__PURE__*/ _jsxs("div", {
                className: tabsContainerWrapper({
                    isCentered
                }),
                children: [
                    isScrollable && isLeftArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                        direction: "left",
                        onClick: ()=>handleScroll('left'),
                        tabListId: tabListId
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: tabsWrapper(),
                        ref: containerRef,
                        role: "tablist",
                        id: tabListId,
                        "aria-label": "Testimonial tab navigation",
                        ...getTestProps(`tab-ul`),
                        children: tabItems.map((item, index)=>{
                            const isActive = activeTab === item.uid;
                            const { namePlaceholder: namePlaceholderActive, titlePlaceholder: titlePlaceholderActive } = SHARED_PLACEHOLDER_VARIANTS({
                                size: 'full',
                                isActive
                            });
                            return /*#__PURE__*/ _jsx("li", {
                                className: tabLinkWrapper({
                                    isActive
                                }),
                                ...getTestProps(`tab-content-${index + 1}`),
                                role: "presentation",
                                children: /*#__PURE__*/ _jsx("button", {
                                    className: tabLink({
                                        isActive,
                                        disabled: false
                                    }),
                                    onClick: ()=>{
                                        setActiveTab(item.uid || '');
                                        // Create and send GTM event
                                        const gtmEvent = createTabClickGTMEvent({
                                            event: 'tab_click',
                                            header: sectionTitle,
                                            ctaText: item.fields?.authorName?.value ?? '',
                                            componentName: 'Tabbed Cards'
                                        });
                                        sendGTMEvent({
                                            ...gtmEvent
                                        });
                                    },
                                    id: `tab-${item.uid}`,
                                    role: "tab",
                                    "aria-controls": `tab-content-${item.uid}`,
                                    "aria-label": item.fields?.authorName?.value || `Tab ${index + 1}`,
                                    "aria-selected": isActive,
                                    ...getTestProps(`tab-link-${index + 1}`),
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: tabHeader(),
                                        children: [
                                            isEditing ? /*#__PURE__*/ _jsx("div", {
                                                className: "max-w-14 max-h-14",
                                                children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                                    field: item.fields?.image,
                                                    className: 'rounded-border-radius-radius-2 overflow-hidden object-cover',
                                                    ...getTestProps(`tab-image-${index + 1}`)
                                                })
                                            }) : /*#__PURE__*/ _jsx(_Fragment, {
                                                children: item.fields?.image?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                                                    field: item.fields.image,
                                                    className: tabImage(),
                                                    ...getTestProps(`tab-image-${index + 1}`)
                                                }) : /*#__PURE__*/ _jsx("div", {
                                                    className: tabImage(),
                                                    children: /*#__PURE__*/ _jsx("div", {
                                                        className: imagePlaceholder(),
                                                        children: /*#__PURE__*/ _jsx(ImageWrapper, {
                                                            field: {
                                                                value: {
                                                                    src: '/assets/profile_placeholder.png',
                                                                    alt: 'No image'
                                                                }
                                                            },
                                                            className: tabImage(),
                                                            ...getTestProps(`related-profile-image-placeholder`)
                                                        })
                                                    })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: tabText(),
                                                children: isEditing ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                            field: item.fields?.authorName,
                                                            ...getTestProps(`tab-author-name-${index + 1}`)
                                                        }),
                                                        /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                            field: item.fields?.authorTitle,
                                                            ...getTestProps(`tab-author-title-${index + 1}`)
                                                        })
                                                    ]
                                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        item.fields?.authorName?.value ? /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                            field: item.fields.authorName,
                                                            ...getTestProps(`tab-author-name-${index + 1}`)
                                                        }) : /*#__PURE__*/ _jsx("div", {
                                                            className: namePlaceholderActive(),
                                                            children: getDictionaryValue('NoName') || 'No Name'
                                                        }),
                                                        item.fields?.authorTitle?.value ? /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                            field: item.fields.authorTitle,
                                                            ...getTestProps(`tab-author-title-${index + 1}`)
                                                        }) : /*#__PURE__*/ _jsx("div", {
                                                            className: titlePlaceholderActive(),
                                                            children: getDictionaryValue('NoTitle') || 'No Title'
                                                        })
                                                    ]
                                                })
                                            })
                                        ]
                                    })
                                })
                            }, item.uid);
                        })
                    }),
                    isScrollable && isRightArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                        direction: "right",
                        onClick: ()=>handleScroll('right'),
                        tabListId: tabListId
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                name: phKey,
                rendering: props.rendering,
                ...getTestProps(`tab-ph-${props?.rendering?.uid}`)
            })
        ]
    });
};
const TestimonialTabs = (props)=>{
    const tabItems = findComponent(props.rendering, 'TestimonialTabItem') || [];
    return /*#__PURE__*/ _jsx(TabContextProvider, {
        tabs: tabItems,
        children: /*#__PURE__*/ _jsx(TestimonialTabContent, {
            ...props
        })
    });
};
export const Default = withStandardComponentWrapper(TestimonialTabs, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'w-full'
        ],
        tabsContainerWrapper: [
            'relative',
            'flex',
            'items-center',
            'justify-start',
            'mb-spacing-spacing-6',
            'mb-component-section-container-gutter',
            '-mr-spacing-spacing-16',
            'sm:-mr-spacing-spacing-20',
            'md:-mr-component-section-container-padding-x'
        ],
        tabsWrapper: [
            'flex',
            'gap-spacing-spacing-8',
            'sm:gap-spacing-spacing-12',
            'md:gap-spacing-spacing-16',
            'overflow-x-auto',
            'scrollbar-width-none',
            'snap-x',
            'min-h-24',
            'snap-mandatory',
            '[-ms-overflow-style:none]',
            '[&::-webkit-scrollbar]:hidden',
            'p-1',
            'scroll-pl-1'
        ],
        tabLinkWrapper: [
            'flex-shrink-0',
            'snap-start',
            'snap-always'
        ],
        tabLink: [
            'flex',
            'items-center',
            'h-full',
            'gap-spacing-spacing-3',
            'px-spacing-spacing-16',
            'py-spacing-spacing-12',
            'text-left',
            'border-4',
            'border-solid',
            'border-transparent',
            'rounded-general-border-radius-button',
            'transition-all',
            'duration-200',
            'cursor-pointer',
            'focus:outline-none',
            'shadow-[0_0_8px_0_rgba(0,0,0,0.10)]'
        ],
        tabHeader: [
            'flex',
            'items-center',
            'gap-spacing-spacing-16'
        ],
        tabImage: [
            'flex',
            '!w-14',
            '!h-14',
            'items-start',
            'gap-spacing-spacing-10',
            'rounded-border-radius-radius-2',
            'overflow-hidden',
            'object-cover'
        ],
        tabText: [
            'flex',
            'flex-col',
            'text-left',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large'
        ],
        tabArrows: [
            'absolute',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'z-10',
            'flex',
            'w-10',
            'h-10',
            'sm:w-11',
            'sm:h-11',
            'md:w-12',
            'md:h-12',
            'p-spacing-spacing-8',
            'sm:p-spacing-spacing-10',
            'md:p-spacing-spacing-12',
            'items-center',
            'gap-spacing-spacing-8',
            'aspect-square',
            'rounded-general-border-radius-button',
            'opacity-80',
            'bg-component-button-primary-bg',
            'hover:opacity-100',
            'active:opacity-100',
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-offset-2',
            'disabled:opacity-50',
            'disabled:cursor-not-allowed'
        ]
    },
    variants: {
        isCentered: {
            true: {
                tabsContainerWrapper: [
                    'justify-center'
                ]
            },
            false: {
                tabsContainerWrapper: [
                    'justify-start'
                ]
            }
        },
        isActive: {
            true: {
                tabLink: [
                    'text-color-text-white',
                    'bg-component-button-primary-inverse-bg-hover',
                    'hover:bg-component-button-primary-inverse-bg-hover',
                    'hover:text-color-text-white',
                    'focus:!border-color-border-brand-2',
                    'focus:bg-component-button-primary-inverse-bg-focused',
                    'focus:text-color-text-white',
                    'active:bg-component-button-primary-inverse-bg-hover',
                    'active:text-color-text-white'
                ]
            },
            false: {
                tabLink: [
                    'bg-color-grayscale-white',
                    'text-component-button-primary-text',
                    'hover:bg-component-button-primary-inverse-bg-hover',
                    'hover:text-color-text-white',
                    'focus:!border-color-border-brand-2',
                    'focus:bg-component-button-primary-inverse-bg-focused',
                    'focus:text-color-text-white',
                    'active:bg-component-button-primary-inverse-bg-hover',
                    'active:text-color-text-white'
                ]
            }
        },
        disabled: {
            true: {
                tabLink: [
                    'opacity-50',
                    'bg-white',
                    'border-color-border-brand-2',
                    'cursor-not-allowed'
                ]
            },
            false: {}
        },
        left: {
            true: {
                tabArrows: [
                    '-left-3',
                    'sm:-left-2',
                    'md:-left-6'
                ]
            },
            false: {}
        },
        right: {
            true: {
                tabArrows: [
                    'right-1',
                    'sm:right-2',
                    'md:right-6'
                ]
            },
            false: {}
        }
    }
});
// Shared placeholder styling for testimonial components
export const SHARED_PLACEHOLDER_VARIANTS = tv({
    slots: {
        imagePlaceholder: [
            'bg-color-grayscale-200',
            'rounded-general-border-radius-2',
            'flex',
            'items-center',
            'justify-center'
        ],
        namePlaceholder: [
            'text-color-text-text-secondary',
            'text-typography-font-size-body-medium',
            'font-typography-font-family-body',
            'font-typography-font-weight-body-medium'
        ],
        titlePlaceholder: [
            'text-color-text-text-secondary',
            'text-typography-font-size-body-small',
            'font-typography-font-family-body'
        ]
    },
    variants: {
        size: {
            full: {
                imagePlaceholder: [
                    'w-full',
                    'h-full'
                ]
            },
            fixed: {
                imagePlaceholder: [
                    'w-14',
                    'h-14'
                ]
            }
        },
        isActive: {
            true: {
                namePlaceholder: [
                    'text-color-text-white',
                    'text-typography-font-size-body-medium',
                    'font-typography-font-family-body',
                    'font-typography-font-weight-body-medium'
                ],
                titlePlaceholder: [
                    'text-color-text-white',
                    'text-typography-font-size-body-small',
                    'font-typography-font-family-body'
                ]
            },
            false: {}
        }
    }
});
